import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  hasUserRespondedToConsent,
  acceptAllCookies,
  acceptNecessaryOnly,
} from "../../utils/cookieConsent";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  background: var(--color-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Banner = styled.div`
  background: var(--color-surface);
  padding: 24px;
  max-width: 520px;
  width: 100%;
  border-radius: 16px;
  box-shadow: var(--shadow-card);

  @media (min-width: 600px) {
    padding: 28px 32px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  font-size: var(--font-size-body-medium);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-body);
  margin: 0;
  color: var(--color-text);
`;

const NecessaryOnlyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: var(--font-size-body-small);
  color: var(--color-text-muted);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--color-text-secondary);
  }
`;

const Description = styled.p`
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  color: var(--color-text-secondary);
  margin: 0 0 20px 0;
`;

const AcceptButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  font-size: var(--font-size-body-medium);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  background: var(--color-text);
  color: var(--color-text-inverse);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const PolicyLink = styled.a`
  text-decoration: underline;
`;

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hasUserRespondedToConsent()) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    acceptAllCookies();
    setVisible(false);
  };

  const handleRejectOptional = () => {
    acceptNecessaryOnly();
    setVisible(false);
  };

  return (
    <Overlay>
      <Banner>
        <TitleRow>
          <Title>{t("cookieConsentBanner.title")}</Title>
          <NecessaryOnlyLink onClick={handleRejectOptional}>
            {t("cookieConsentBanner.acceptNecessaryOnly")}
          </NecessaryOnlyLink>
        </TitleRow>
        <Description>
          {t("cookieConsentBanner.descriptionBeforeLink")}{" "}
          <PolicyLink href="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer">
            {t("cookieConsentBanner.policyLinkText")}
          </PolicyLink>
          {t("cookieConsentBanner.descriptionAfterLink")}
        </Description>
        <AcceptButton onClick={handleAcceptAll}>
          {t("cookieConsentBanner.acceptAll")}
        </AcceptButton>
      </Banner>
    </Overlay>
  );
};
